import { forwardRef } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";

/**
 * Renders a MUI {@link Button} wired to `href` via react-router-dom {@link RouterLink}.
 * Primary click uses client-side navigation; modified clicks (Ctrl/Cmd/middle mouse)
 * and the context menu behave like a normal link (e.g. open in new tab).
 * Additional props are forwarded to MUI `Button`.
 *
 * @param {Object} props
 * @param {string|import("react-router-dom").To} props.href - Path passed to the router as `to`.
 * @param {React.ReactNode} props.children - Button content.
 */
const HrefButton = forwardRef(function HrefButton({ href, children, ...buttonProps }, ref) {
  return (
    <Button ref={ref} component={RouterLink} to={href} {...buttonProps}>
      {children}
    </Button>
  );
});

HrefButton.displayName = "HrefButton";

HrefButton.propTypes = {
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  children: PropTypes.node,
};

/**
 * Same router-link semantics as {@link HrefButton}, for {@link IconButton}.
 * Additional props are forwarded to MUI `IconButton`.
 *
 * @param {Object} props
 * @param {string|import("react-router-dom").To} props.href - Path passed to the router as `to`.
 * @param {React.ReactNode} props.children - Icon content.
 */
const HrefIconButton = forwardRef(function HrefIconButton({ href, children, ...iconButtonProps }, ref) {
  return (
    <IconButton ref={ref} component={RouterLink} to={href} {...iconButtonProps}>
      {children}
    </IconButton>
  );
});

HrefIconButton.displayName = "HrefIconButton";

HrefIconButton.propTypes = {
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  children: PropTypes.node,
};

/**
 * Same layout as {@link HrefIconButton}, but navigates back in browser history via {@link useNavigate}.
 * Use when the target route is not known upfront (e.g. top-bar back affordance).
 * Additional props are forwarded to MUI `IconButton`.
 *
 * @param {Object} props
 * @param {number} [props.steps=-1] - Number of history steps to go back.
 * @param {React.ReactNode} props.children - Icon content.
 */
const HistoryBackIconButton = forwardRef(function HistoryBackIconButton(
  { steps = -1, children, onClick, ...iconButtonProps },
  ref,
) {
  const navigate = useNavigate();

  function handleClick(event) {
    onClick?.(event);
    if (!event.defaultPrevented) {
      navigate(steps);
    }
  }

  return (
    <IconButton ref={ref} onClick={handleClick} {...iconButtonProps}>
      {children}
    </IconButton>
  );
});

HistoryBackIconButton.displayName = "HistoryBackIconButton";

HistoryBackIconButton.propTypes = {
  steps: PropTypes.number,
  children: PropTypes.node,
  onClick: PropTypes.func,
};

/**
 * Same router-link semantics as {@link HrefButton}, for {@link MenuItem}.
 * Use inside MUI `Menu` / `Dropdown` for navigation-only rows; combine with `onClick` to close the menu.
 * Additional props are forwarded to MUI `MenuItem`.
 *
 * @param {Object} props
 * @param {string|import("react-router-dom").To} props.href - Path passed to the router as `to`.
 * @param {React.ReactNode} props.children - Row content (e.g. `ListItemIcon` + label).
 */
const HrefMenuItem = forwardRef(function HrefMenuItem({ href, children, ...menuItemProps }, ref) {
  return (
    <MenuItem ref={ref} component={RouterLink} to={href} {...menuItemProps}>
      {children}
    </MenuItem>
  );
});

HrefMenuItem.displayName = "HrefMenuItem";

HrefMenuItem.propTypes = {
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  children: PropTypes.node,
};

export { HrefIconButton, HrefMenuItem, HistoryBackIconButton };
export default HrefButton;
